// Murano Product Matcher — fetch + filter + score logic.
//
// Source set is built from one or more Shopify Murano collections. We then
// apply optional filters (colour / size / price), score each product, and
// progressively soften the filters if the result set is too small.

import { fetchCollection, type ShopifyProduct } from "./shopify";
import { COLOR_CATEGORIES } from "./colors";
import { SIZE_BUCKETS, parseDiameterCm } from "./sizes";

export type MuranoType = "loft" | "mushroom" | "wall" | "any";
export type Room = "dining" | "living" | "bedroom" | "bath" | "any";
export type ColorSlug =
  | "lyserod"
  | "gul"
  | "gron"
  | "blaa"
  | "rod"
  | "orange"
  | "lilla"
  | "hvid"
  | "any";
export type SizeSlug = "kompakt" | "klassisk" | "stor" | "imponerende" | "any";
export type PriceBand = "under2500" | "mid" | "high" | "premium" | "any";

// New: a "wall look" reading instead of a hard colour pick. Maps softly to
// colour family bias during scoring; keeps the hard `color` filter optional.
export type WallLook =
  | "cream"     // cream / chalky white walls → warm pastels
  | "pastel"   // soft pastels → multicolour / clear
  | "earth"    // dusty earth tones → terracotta / yellow / orange
  | "moody"    // moody dark walls → blue / purple / red
  | "raw"      // raw plaster / brick → white / opal
  | "clash"    // I love a clash → no bias, multicolour boost
  | "any";

// New: deal-breaker — mostly affects copy on the result page, with a tiny
// scoring nudge for in-stock items.
export type DealBreaker =
  | "today"     // must be in stock today
  | "monday"    // must ship before next Monday
  | "patient"   // can wait for the right one
  | "any";

export type MatchAnswers = {
  type: MuranoType;
  room: Room;
  color: ColorSlug;
  size: SizeSlug;
  price: PriceBand;
  wall: WallLook;
  deal: DealBreaker;
};

export const TYPE_TO_HANDLE: Record<Exclude<MuranoType, "any">, string> = {
  loft: "murano-loftlamper",
  mushroom: "murano-mushroom-lampe",
  wall: "murano-vaeglamper",
};

export const PRICE_BANDS: Record<
  Exclude<PriceBand, "any">,
  { min: number; max: number }
> = {
  under2500: { min: 0, max: 2500 },
  mid: { min: 2500, max: 4500 },
  high: { min: 4500, max: 7500 },
  premium: { min: 7500, max: Infinity },
};

// Soft colour bias by wall look. These are NOT hard filters — they award a
// small score boost when no explicit color is picked.
export const WALL_TO_COLOR_BIAS: Record<WallLook, ColorSlug[]> = {
  cream: ["lyserod", "gul", "gron"],
  pastel: ["lyserod", "gron", "blaa"],
  earth: ["orange", "rod", "gul"],
  moody: ["blaa", "lilla", "rod"],
  raw: ["hvid", "lyserod"],
  clash: [],
  any: [],
};

const MIN_RESULTS = 6;

function dedupe(products: ShopifyProduct[]): ShopifyProduct[] {
  const seen = new Set<string>();
  const out: ShopifyProduct[] = [];
  for (const p of products) {
    if (seen.has(p.node.handle)) continue;
    seen.add(p.node.handle);
    out.push(p);
  }
  return out;
}

async function fetchSourceSet(type: MuranoType): Promise<ShopifyProduct[]> {
  if (type !== "any") {
    const handle = TYPE_TO_HANDLE[type];
    const coll = await fetchCollection(handle, 60).catch(() => null);
    return coll?.products?.edges ?? [];
  }
  const handles = Object.values(TYPE_TO_HANDLE);
  const results = await Promise.all(
    handles.map((h) => fetchCollection(h, 40).catch(() => null)),
  );
  const all: ShopifyProduct[] = [];
  for (const r of results) if (r?.products?.edges) all.push(...r.products.edges);
  return dedupe(all);
}

// ---------- Predicates ----------

function colorMatches(p: ShopifyProduct, color: ColorSlug): boolean {
  if (color === "any") return true;
  const cat = COLOR_CATEGORIES.find((c) => c.slug === color);
  if (!cat) return true;
  const text = `${p.node.title} ${p.node.description ?? ""}`.toLowerCase();
  const terms = cat.query
    .split(/\s+OR\s+/i)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  return terms.some((t) => text.includes(t));
}

function sizeMatches(p: ShopifyProduct, size: SizeSlug): boolean {
  if (size === "any") return true;
  const bucket = SIZE_BUCKETS.find((b) => b.slug === size);
  if (!bucket) return true;
  const d = parseDiameterCm({
    title: p.node.title,
    description: p.node.description,
    handle: p.node.handle,
  });
  if (d == null) return false;
  return d >= bucket.min && d < bucket.max;
}

function priceMatches(p: ShopifyProduct, price: PriceBand): boolean {
  if (price === "any") return true;
  const band = PRICE_BANDS[price];
  if (!band) return true;
  const amount = parseFloat(p.node.priceRange.minVariantPrice.amount);
  if (Number.isNaN(amount)) return false;
  return amount >= band.min && amount < band.max;
}

// ---------- Scoring ----------

function score(p: ShopifyProduct, a: MatchAnswers): number {
  let s = 0;
  if (colorMatches(p, a.color)) s += 3;
  if (sizeMatches(p, a.size)) s += 2;
  if (priceMatches(p, a.price)) s += 1;
  if (p.node.availableForSale) s += 1;

  // Wall-look soft bias: only when no hard colour pick has been made.
  if (a.color === "any" && a.wall !== "any") {
    const biased = WALL_TO_COLOR_BIAS[a.wall];
    for (const slug of biased) {
      if (colorMatches(p, slug)) {
        s += 1.5;
        break;
      }
    }
    // "Clash" rewards multicolour — title / description mentioning multi.
    if (a.wall === "clash") {
      const text = `${p.node.title} ${p.node.description ?? ""}`.toLowerCase();
      if (/multi|multicolor|multifarv|swirl|harlequin|patchwork/.test(text)) s += 1.5;
    }
  }

  // Deal-breaker nudge: prefer in-stock when user wants it now.
  if ((a.deal === "today" || a.deal === "monday") && p.node.availableForSale) {
    s += 0.5;
  }

  return s;
}

function tieBreak(a: ShopifyProduct, b: ShopifyProduct): number {
  const ad = a.node.createdAt ? Date.parse(a.node.createdAt) : 0;
  const bd = b.node.createdAt ? Date.parse(b.node.createdAt) : 0;
  return bd - ad;
}

// ---------- Public API ----------

export type MatchResult = {
  products: ShopifyProduct[];
  total: number;
  softened: Array<"price" | "size" | "color">;
};

export async function fetchCandidates(
  answers: MatchAnswers,
): Promise<MatchResult> {
  const source = await fetchSourceSet(answers.type);
  if (source.length === 0) {
    return { products: [], total: 0, softened: [] };
  }

  // Try strict, then soften progressively while keeping `type` intact.
  const ladder: Array<{ a: MatchAnswers; dropped?: "price" | "size" | "color" }> = [
    { a: answers },
    { a: { ...answers, price: "any" }, dropped: "price" },
    { a: { ...answers, price: "any", size: "any" }, dropped: "size" },
    {
      a: { ...answers, price: "any", size: "any", color: "any" },
      dropped: "color",
    },
  ];

  const softened: Array<"price" | "size" | "color"> = [];
  let filtered: ShopifyProduct[] = [];

  for (const step of ladder) {
    filtered = source.filter(
      (p) =>
        colorMatches(p, step.a.color) &&
        sizeMatches(p, step.a.size) &&
        priceMatches(p, step.a.price),
    );
    if (step.dropped) softened.push(step.dropped);
    if (filtered.length >= MIN_RESULTS) break;
  }

  // Score against the *original* preferences so the best matches still float
  // to the top even after softening.
  const ranked = filtered
    .map((p) => ({ p, s: score(p, answers) }))
    .sort((x, y) => (y.s - x.s) || tieBreak(x.p, y.p))
    .map((x) => x.p);

  const baseList =
    ranked.length > 0
      ? ranked
      : source
          .map((p) => ({ p, s: score(p, answers) }))
          .sort((x, y) => (y.s - x.s) || tieBreak(x.p, y.p))
          .map((x) => x.p)
          .slice(0, 24);

  // Diversify the top of the list so the user doesn't see 8 near-identical
  // lamps in a row when the source set leans heavily toward one colour or
  // size. Keeps the underlying ranking intact for ties.
  const finalProducts = diversify(baseList, 24);

  return {
    products: finalProducts,
    total: finalProducts.length,
    softened,
  };
}

// ---------- Diversification ----------
//
// Greedy round-robin: walks the ranked list and picks the next item whose
// (colour-family, size-bucket) pair we haven't seen yet, until every pair is
// used once — then resets the "seen" set and continues. This preserves the
// score order within each diversity pass.

function diversifyKey(p: ShopifyProduct): string {
  const text = `${p.node.title} ${p.node.description ?? ""}`.toLowerCase();
  let color = "x";
  for (const c of COLOR_CATEGORIES) {
    const terms = c.query.split(/\s+OR\s+/i).map((t) => t.trim().toLowerCase());
    if (terms.some((t) => t && text.includes(t))) {
      color = c.slug;
      break;
    }
  }
  const d = parseDiameterCm({
    title: p.node.title,
    description: p.node.description,
    handle: p.node.handle,
  });
  let size = "x";
  if (d != null) {
    for (const b of SIZE_BUCKETS) {
      if (d >= b.min && d < b.max) { size = b.slug; break; }
    }
  }
  return `${color}|${size}`;
}

function diversify(list: ShopifyProduct[], limit: number): ShopifyProduct[] {
  if (list.length <= 4) return list.slice(0, limit);
  const out: ShopifyProduct[] = [];
  const remaining = list.slice();
  let seen = new Set<string>();
  while (out.length < limit && remaining.length > 0) {
    let pickedIdx = -1;
    for (let i = 0; i < remaining.length; i++) {
      const key = diversifyKey(remaining[i]);
      if (!seen.has(key)) {
        pickedIdx = i;
        seen.add(key);
        break;
      }
    }
    if (pickedIdx === -1) {
      // Every key has been used in this pass — reset and pick the highest-ranked remaining.
      seen = new Set();
      pickedIdx = 0;
      seen.add(diversifyKey(remaining[0]));
    }
    out.push(remaining[pickedIdx]);
    remaining.splice(pickedIdx, 1);
  }
  return out;
}

// Default answers for an "unanswered" URL.
export const DEFAULT_ANSWERS: MatchAnswers = {
  type: "any",
  room: "any",
  color: "any",
  size: "any",
  price: "any",
  wall: "any",
  deal: "any",
};

export function parseAnswersFromSearch(
  search: Record<string, string | undefined>,
): MatchAnswers {
  const type = (["loft", "mushroom", "wall", "any"] as const).find(
    (v) => v === search.type,
  ) ?? "any";
  const room = (["dining", "living", "bedroom", "bath", "any"] as const).find(
    (v) => v === search.room,
  ) ?? "any";
  const color =
    (["lyserod", "gul", "gron", "blaa", "rod", "orange", "lilla", "hvid", "any"] as const).find(
      (v) => v === search.color,
    ) ?? "any";
  const size = (["kompakt", "klassisk", "stor", "imponerende", "any"] as const).find(
    (v) => v === search.size,
  ) ?? "any";
  const price = (["under2500", "mid", "high", "premium", "any"] as const).find(
    (v) => v === search.price,
  ) ?? "any";
  const wall = (["cream", "pastel", "earth", "moody", "raw", "clash", "any"] as const).find(
    (v) => v === search.wall,
  ) ?? "any";
  const deal = (["today", "monday", "patient", "any"] as const).find(
    (v) => v === search.deal,
  ) ?? "any";
  return { type, room, color, size, price, wall, deal };
}

export function hasAnyAnswer(a: MatchAnswers): boolean {
  return (
    a.type !== "any" ||
    a.color !== "any" ||
    a.size !== "any" ||
    a.price !== "any" ||
    a.wall !== "any" ||
    a.deal !== "any"
  );
}

// ---------- Blurb builder ----------
//
// Builds a short, written-feeling sentence from the answers. Used as the
// result-page heading so it reads like a note from the shop, not a count.

type Locale = "da" | "en";

const PLACEMENT_DA: Record<MuranoType, string> = {
  loft: "over spisebordet",
  mushroom: "til konsollen",
  wall: "ved sengen",
  any: "til dit hjem",
};
const PLACEMENT_EN: Record<MuranoType, string> = {
  loft: "above the dining table",
  mushroom: "for the sideboard",
  wall: "beside the bed",
  any: "for your home",
};

const FEELING_DA: Record<SizeSlug, string> = {
  kompakt: "noget stille",
  klassisk: "en hverdagsven",
  stor: "et stykke der samler rummet",
  imponerende: "et rigtigt blikfang",
  any: "noget særligt",
};
const FEELING_EN: Record<SizeSlug, string> = {
  kompakt: "something quiet",
  klassisk: "an everyday companion",
  stor: "a piece that pulls the room together",
  imponerende: "a proper showpiece",
  any: "something special",
};

const WALL_TONE_DA: Record<WallLook, string> = {
  cream: "i varme pasteller",
  pastel: "i bløde nuancer",
  earth: "i jordede toner",
  moody: "i dybe farver",
  raw: "i opal og hvid",
  clash: "med karakter og farve",
  any: "",
};
const WALL_TONE_EN: Record<WallLook, string> = {
  cream: "in warm pastels",
  pastel: "in soft tones",
  earth: "in earthy hues",
  moody: "in deep colours",
  raw: "in opal and white",
  clash: "with character and colour",
  any: "",
};

const DEAL_DA: Record<DealBreaker, string> = {
  today: "klar i dag",
  monday: "klar til mandagens forsendelse",
  patient: "værd at vente på",
  any: "",
};
const DEAL_EN: Record<DealBreaker, string> = {
  today: "ready today",
  monday: "ready for Monday's dispatch",
  patient: "worth the wait",
  any: "",
};

export function buildResultBlurb(a: MatchAnswers, locale: Locale): string {
  if (locale === "da") {
    const parts: string[] = [];
    parts.push(`Til dig der vil have ${FEELING_DA[a.size]} ${PLACEMENT_DA[a.type]}`);
    const tone = WALL_TONE_DA[a.wall];
    if (tone) parts.push(tone);
    const deal = DEAL_DA[a.deal];
    if (deal) parts.push(deal);
    return parts.join(" — ") + ".";
  }
  const parts: string[] = [];
  parts.push(`For ${FEELING_EN[a.size]} ${PLACEMENT_EN[a.type]}`);
  const tone = WALL_TONE_EN[a.wall];
  if (tone) parts.push(tone);
  const deal = DEAL_EN[a.deal];
  if (deal) parts.push(deal);
  return parts.join(" — ") + ".";
}

// ---------- Per-product explanation ("Why this one?") ----------
//
// Returns short reason chips describing which of the user's answers this
// product satisfies. Used for the expandable disclosure on each result card.

export type MatchReason = {
  kind: "type" | "color" | "wall" | "size" | "price" | "deal" | "fallback";
  label: string;
  hit: boolean;
};

const TYPE_LABEL_DA: Record<Exclude<MuranoType, "any">, string> = {
  loft: "Loft / pendel",
  mushroom: "Bord / fungo",
  wall: "Væglampe",
};
const TYPE_LABEL_EN: Record<Exclude<MuranoType, "any">, string> = {
  loft: "Ceiling / pendant",
  mushroom: "Table / mushroom",
  wall: "Wall lamp",
};

const PRICE_LABEL_DA: Record<Exclude<PriceBand, "any">, string> = {
  under2500: "Under 2.500 kr.",
  mid: "2.500–4.500 kr.",
  high: "4.500–7.500 kr.",
  premium: "7.500 kr.+",
};
const PRICE_LABEL_EN: Record<Exclude<PriceBand, "any">, string> = {
  under2500: "Under 2,500 DKK",
  mid: "2,500–4,500 DKK",
  high: "4,500–7,500 DKK",
  premium: "7,500 DKK+",
};

const WALL_LABEL_DA: Record<Exclude<WallLook, "any">, string> = {
  cream: "Passer cremehvide vægge",
  pastel: "Passer bløde pastelvægge",
  earth: "Passer jordede toner",
  moody: "Passer mørke vægge",
  raw: "Passer rå vægge",
  clash: "Bringer farveklang",
};
const WALL_LABEL_EN: Record<Exclude<WallLook, "any">, string> = {
  cream: "Suits cream walls",
  pastel: "Suits pastel walls",
  earth: "Suits earthy walls",
  moody: "Suits moody walls",
  raw: "Suits raw walls",
  clash: "Brings the clash",
};

export function explainMatch(
  p: ShopifyProduct,
  a: MatchAnswers,
  locale: Locale,
): MatchReason[] {
  const reasons: MatchReason[] = [];

  // Type / placement — by construction the source set comes from the picked collection.
  if (a.type !== "any") {
    reasons.push({
      kind: "type",
      label: locale === "da" ? TYPE_LABEL_DA[a.type] : TYPE_LABEL_EN[a.type],
      hit: true,
    });
  }

  // Hard colour pick
  if (a.color !== "any") {
    const cat = COLOR_CATEGORIES.find((c) => c.slug === a.color);
    if (cat) {
      reasons.push({
        kind: "color",
        label: locale === "da" ? cat.labelDa : cat.labelEn,
        hit: colorMatches(p, a.color),
      });
    }
  }

  // Wall-look soft bias (only meaningful when no hard colour pick was made)
  if (a.color === "any" && a.wall !== "any") {
    const biased = WALL_TO_COLOR_BIAS[a.wall];
    let biasHit = biased.some((slug) => colorMatches(p, slug));
    if (a.wall === "clash") {
      const text = `${p.node.title} ${p.node.description ?? ""}`.toLowerCase();
      if (/multi|multicolor|multifarv|swirl|harlequin|patchwork/.test(text)) biasHit = true;
    }
    if (biasHit) {
      reasons.push({
        kind: "wall",
        label: locale === "da" ? WALL_LABEL_DA[a.wall] : WALL_LABEL_EN[a.wall],
        hit: true,
      });
    }
  }

  // Size
  if (a.size !== "any") {
    const bucket = SIZE_BUCKETS.find((b) => b.slug === a.size);
    if (bucket) {
      const hit = sizeMatches(p, a.size);
      const sizeLabel = locale === "da" ? bucket.labelDa : bucket.labelEn;
      reasons.push({
        kind: "size",
        label: hit
          ? sizeLabel
          : locale === "da" ? `Lidt ud over "${sizeLabel}"` : `Slightly outside "${sizeLabel}"`,
        hit,
      });
    }
  }

  // Price
  if (a.price !== "any") {
    reasons.push({
      kind: "price",
      label: locale === "da" ? PRICE_LABEL_DA[a.price] : PRICE_LABEL_EN[a.price],
      hit: priceMatches(p, a.price),
    });
  }

  // Deal-breaker (in-stock relevance)
  if (a.deal === "today" || a.deal === "monday") {
    if (p.node.availableForSale) {
      reasons.push({
        kind: "deal",
        label: locale === "da"
          ? a.deal === "today" ? "På lager nu" : "Klar til mandagens forsendelse"
          : a.deal === "today" ? "In stock now" : "Ready for Monday's dispatch",
        hit: true,
      });
    } else {
      reasons.push({
        kind: "deal",
        label: locale === "da" ? "Ikke på lager — særligt stykke" : "Not in stock — special piece",
        hit: false,
      });
    }
  }

  // Fallback when every answer was "any".
  if (reasons.length === 0) {
    reasons.push({
      kind: "fallback",
      label: locale === "da" ? "Et kuratorvalg fra Murano" : "A curator's pick from Murano",
      hit: true,
    });
  }

  return reasons;
}

